import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  SxProps,
} from "@mui/material";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { ReactNode, useMemo } from "react";
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";

import { DisplayOnlyField } from "./DisplayOnlyField";
import { ErrorDisplay } from "./ErrorDisplay";
import { TextWithTooltip } from "./TextWithTooltip";

type Props<T extends FieldValues> = {
  label: string;
  required?: boolean;
  tooltip?: ReactNode;
  subtitle?: string;
  radioOptions: { value: any; label: string }[];
  disabled?: boolean;
  hideErrors?: boolean;
  fullWidth?: boolean;
  /** Use row layout for the radio buttons (default: `true`) */
  row?: boolean;
  sx?: SxProps;
  /** direction of radio buttons and CE errors. Defaults to row */
  direction?: "row" | "column";
} & Partial<Pick<ControllerRenderProps, "onChange">> &
  UseControllerProps<T>;

export function RadioGroupField<T extends FieldValues>(props: Props<T>) {
  const inputLabelId = useMemo(() => `${props.name}label`, [props.name]);
  const labelWithTooltip = useMemo(
    () =>
      props.tooltip ? <TextWithTooltip text={props.label} tooltip={props.tooltip} /> : props.label,
    [props.label, props.tooltip]
  );

  const formContext = useValidatingFormContext();
  if (formContext?.metadata?.isDisplayOnly) {
    const value = formContext?.getValues(props.name);
    const text = props.radioOptions.find((option) => option.value === value)?.label;
    return <DisplayOnlyField text={text ?? ""} label={props.label} />;
  }

  return (
    <FormControl
      fullWidth={props.fullWidth === undefined ? true : props.fullWidth}
      sx={{ ...props.sx }}
    >
      <FormLabel id={inputLabelId} required={props.required}>
        {labelWithTooltip}
      </FormLabel>
      {props.subtitle && <FormHelperText sx={{ ml: 0 }}>{props.subtitle}</FormHelperText>}
      <Controller
        name={props.name}
        control={props.control}
        rules={props.rules}
        render={({ field, fieldState: { error } }) => {
          return (
            <>
              <RadioGroup
                aria-labelledby={inputLabelId}
                {...field}
                value={field.value ?? ""}
                onChange={(e) => {
                  const newVal = props.radioOptions.find(
                    // The radio button input event provides a string
                    (ro) => ro.value.toString() === e.target.value
                  );
                  field.onChange(newVal?.value);
                  if (props.onChange) {
                    props.onChange(newVal);
                  }
                }}
                row={props.row ?? true}
              >
                <Stack
                  direction={props.direction ?? "row"}
                  alignItems={props.direction === "row" ? "center" : "flex-start"}
                >
                  {props.radioOptions.map(({ label, value }, index) => (
                    <FormControlLabel
                      key={index}
                      control={<Radio />}
                      label={label}
                      value={value}
                      disabled={props.disabled}
                    />
                  ))}
                </Stack>
              </RadioGroup>
              <ErrorDisplay error={error} hideErrors={props.hideErrors} />
            </>
          );
        }}
      />
    </FormControl>
  );
}
